import React, { useEffect, useState } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';

import { usePutAcquisitionsMutation } from '../../../store/slices/apiSlice/acquisitions-api-slice.js';
import { datagridsx } from '../../../constants/datagridsx.js';
import TableButton from '../components/acquisitionsTableButton.js';
import { handleBackendError } from '../../../utilities/errorHandling.js';
import { toastSuccess } from '../../../utilities/toast.js';
import { formatDateMMDDYYPacific } from '../../../utilities/dateHelpers';

const DateColumnWidth = 140;
const AddressColumnWidth = 200;
const columnDefinitions = [
    {
        field: 'saleDate',
        headerName: 'Sale Date',
        type: 'date',
        editable: true,
        width: DateColumnWidth,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => (
            <span>{formatDateMMDDYYPacific(params.value)}</span>
        ),
    },
    {
        field: 'address',
        headerName: 'Address',
        type: 'string',
        flex: 1,
        minWidth: AddressColumnWidth,
    },
];

function salesDatesToTableData(salesDates) {
    return salesDates.map((sd) => ({ ...sd, saleDate: new Date(sd.saleDate) }));
}

const AcquisitionsSalesDates = ({ salesDates }) => {
    const [updateAcquisitions, updateAcquisitionsResponse] =
    usePutAcquisitionsMutation();
    const [tableRows, setTableRows] = useState(salesDatesToTableData(salesDates));
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        if (updateAcquisitionsResponse) {
            const { isError, status, isSuccess } = updateAcquisitionsResponse;
            if (isError) {
                handleBackendError(
                    'Update Acquisitions Error',
                    updateAcquisitionsResponse
                );
            } else if (status === 'fulfilled' && isSuccess) {
                toastSuccess('Acquisitions Updated');
            }
        }
    }, [updateAcquisitionsResponse]);

    const handleSave = () => {
        const updatedDates = tableRows.map((tr) => ({
            id: tr.id,
            saleDate: tr.saleDate.toISOString(),
        }));
        updateAcquisitions({ salesActualDates: [...updatedDates] });
        setDirty(false);
    };

    const handleReset = () => {
        setTableRows(salesDatesToTableData(salesDates));
        setDirty(false);
    };

    return (
        <div className="acquisitions-salesdates-container">
            <DataGridPro
                rows={tableRows}
                columns={columnDefinitions}
                editMode="cell"
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'saleDate', sort: 'asc' }],
                    },
                }}
                processRowUpdate={(newRow, oldRow) => {
                    if (JSON.stringify(newRow) !== JSON.stringify(oldRow)) {
                        setTableRows((prev) =>
                            prev.map((r) => (r.id === newRow.id ? newRow : r))
                        );
                        setDirty(true);
                    }
                    return newRow;
                }}
                experimentalFeatures={{ newEditingApi: true }}
                sx={{
                    ...datagridsx,
                    '& .MuiDataGrid-footerContainer': {
                        display: 'none',
                    },
                }}
            />
            <div className="mt-4 w-100 d-flex justify-content-center align-items-center gap-4">
                <TableButton label="Save" disabled={!dirty} onClick={handleSave} />
                <TableButton label="Reset" disabled={!dirty} onClick={handleReset} />
            </div>
        </div>
    );
};

export default AcquisitionsSalesDates;
